export const createImage = (url: string) => {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (err) => reject(err));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
};

const getRadianAngle = (degreeValue: number) => {
  return (degreeValue * Math.PI) / 180;
};

export const cropImageToPng = async (params: {
  imageSrc: string;
  pixelCrop: { x: number; y: number; width: number; height: number };
  rotation: number;
  targetWidth?: number;
  targetHeight?: number;
}): Promise<Blob> => {
  const image = await createImage(params.imageSrc);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not create canvas context');
  }

  const rotRad = getRadianAngle(params.rotation);
  const sin = Math.abs(Math.sin(rotRad));
  const cos = Math.abs(Math.cos(rotRad));
  const bBoxWidth = image.width * cos + image.height * sin;
  const bBoxHeight = image.width * sin + image.height * cos;

  const safeCanvas = document.createElement('canvas');
  const safeCtx = safeCanvas.getContext('2d');
  if (!safeCtx) {
    throw new Error('Could not create canvas context');
  }

  safeCanvas.width = Math.ceil(bBoxWidth);
  safeCanvas.height = Math.ceil(bBoxHeight);

  safeCtx.translate(safeCanvas.width / 2, safeCanvas.height / 2);
  safeCtx.rotate(rotRad);
  safeCtx.translate(-image.width / 2, -image.height / 2);
  safeCtx.drawImage(image, 0, 0);

  // Set canvas size to target dimensions if provided, otherwise use crop dimensions
  canvas.width = params.targetWidth || Math.round(params.pixelCrop.width);
  canvas.height = params.targetHeight || Math.round(params.pixelCrop.height);

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    safeCanvas,
    Math.round(params.pixelCrop.x),
    Math.round(params.pixelCrop.y),
    Math.round(params.pixelCrop.width),
    Math.round(params.pixelCrop.height),
    0,
    0,
    canvas.width,
    canvas.height
  );

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (!b) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(b);
    }, 'image/png');
  });

  return blob;
};
